// Orchestrates the Coach view's immersive mode:
//   - toggles the `immersive-coach` body class (drives globals.css)
//   - requests browser fullscreen and locks portrait orientation where supported
//   - mirrors the immersive flag into AppStateContext so App.js can hide chrome
//
// Called from CoachView with `active` driven by coachState.status. When the user
// taps the exit button, the returned `exit()` opts out for the rest of the
// current run; opt-out is cleared the next time `active` flips back to true.

import { useCallback, useEffect, useRef } from "react";
import { getWorkoutSettings } from "./useWorkoutSettings";

const BODY_CLASS = "immersive-coach";

// Auto-enter is mobile-only. Desktop users opt in via the Maximize2 button.
// Breakpoint matches Tailwind `md` (used elsewhere for mobile/desktop UI split).
const isMobileViewport = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  // A phone on its side (#149) is wider than 768 px but still a phone.
  return window.matchMedia("(max-width: 768px), (max-height: 500px)").matches;
};

const tryRequestFullscreen = async () => {
  if (typeof document === "undefined") return;
  if (document.fullscreenElement) return;
  const el = document.documentElement;
  if (!el?.requestFullscreen) return;
  try {
    await el.requestFullscreen();
  } catch {
    // Denied (no user gesture) or unsupported (iOS Safari on iPhone)
  }
};

const tryExitFullscreen = async () => {
  if (typeof document === "undefined") return;
  if (!document.fullscreenElement) return;
  try {
    await document.exitFullscreen();
  } catch {
    // ignore
  }
};

const tryLockPortrait = async () => {
  const orientation = window.screen?.orientation;
  if (!orientation?.lock) return;
  try {
    await orientation.lock("portrait");
  } catch {
    // Lock requires fullscreen/PWA on most browsers; ignore failure.
  }
};

const tryUnlockOrientation = () => {
  const orientation = window.screen?.orientation;
  if (!orientation?.unlock) return;
  try {
    orientation.unlock();
  } catch {
    // ignore
  }
};

const useImmersiveCoach = ({ active, setImmersive }) => {
  const optedOutRef = useRef(false);
  const wasActiveRef = useRef(false);
  // Latest setter, so the unmount-only effect below resets the context flag
  // without re-subscribing on every render.
  const setImmersiveRef = useRef(setImmersive);
  setImmersiveRef.current = setImmersive;

  const enter = useCallback(async () => {
    document.body.classList.add(BODY_CLASS);
    setImmersive(true);
    await tryRequestFullscreen();
    // Only when the screen should stay upright (#149); otherwise the Coach
    // has a landscape layout and turns with the phone.
    if (getWorkoutSettings().coachOrientation === "portrait") await tryLockPortrait();
  }, [setImmersive]);

  const leave = useCallback(async () => {
    document.body.classList.remove(BODY_CLASS);
    setImmersive(false);
    tryUnlockOrientation();
    await tryExitFullscreen();
  }, [setImmersive]);

  const exit = useCallback(() => {
    optedOutRef.current = true;
    leave();
  }, [leave]);

  // Manual re-entry — clears opt-out so subsequent browser-driven exits
  // (ESC, app-switch) don't permanently block this session.
  const enterManual = useCallback(() => {
    optedOutRef.current = false;
    enter();
  }, [enter]);

  // Drive enter/leave from `active` transitions only — re-renders while active
  // stays the same must not re-trigger fullscreen requests. Auto-entry is
  // mobile-only; desktop users opt in via the Maximize2 button.
  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = active;

    if (!wasActive && active && !optedOutRef.current && isMobileViewport()) {
      enter();
    } else if (wasActive && !active) {
      optedOutRef.current = false;
      leave();
    }
  }, [active, enter, leave]);

  // If the user exits fullscreen via ESC / system gesture, treat it as opting
  // out for the rest of the session so we don't immediately re-enter.
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement && wasActiveRef.current) {
        optedOutRef.current = true;
        document.body.classList.remove(BODY_CLASS);
        setImmersive(false);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFsChange);
  }, [setImmersive]);

  // Best-effort cleanup on unmount. This fires when the workout ends and the
  // active adapter unmounts (appState → "finished" swaps in SessionCompleteModal)
  // as well as when the user navigates away mid-workout. The active→false
  // transition's leave() never runs in that case — only this cleanup does — so
  // we must reset the context flag here too, otherwise isImmersive stays true
  // and App.js keeps the header/nav hidden, leaving the user with no way out.
  useEffect(() => {
    return () => {
      document.body.classList.remove(BODY_CLASS);
      setImmersiveRef.current(false);
      tryUnlockOrientation();
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      }
    };
  }, []);

  return { enter: enterManual, exit };
};

export default useImmersiveCoach;
